import React, { useState, useEffect } from 'react';
import { patientService } from '../../services/patientService';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { getApiError } from '../../utils/apiError';

interface FinishTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
  onSuccess?: () => void;
}

const FINISH_REASONS = [
  'Clinic decision',
  'Patient decision',
];

export const FinishTreatmentModal: React.FC<FinishTreatmentModalProps> = ({
  isOpen,
  onClose,
  patientId,
  patientName,
  onSuccess,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!selectedReason) {
      setError('Please select a finish reason');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await patientService.finishPatient(patientId, {
        finish_reason: selectedReason,
        finish_notes: notes.trim() || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'An error occurred') || err.message || 'Failed to finish treatment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!isSaving) onClose();
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleCancel}
      title="Finish Reason"
      isLoading={isSaving}
    >
      <div className="space-y-4">
        <div className="text-gray-700 font-medium">
          Finishing treatment for: <strong>{patientName}</strong>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-2">Reason *</label>
          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            disabled={isSaving}
            className={`${POPUP_STYLES.input} appearance-none`}
          >
            <option value="">-- Select a reason --</option>
            {FINISH_REASONS.map((reason) => (
              <option key={reason} value={reason}>{reason}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-2">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSaving}
            placeholder="Optional notes..."
            rows={4}
            className={`${POPUP_STYLES.input} resize-none`}
          />
        </div>

        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSave}
            disabled={!selectedReason || isSaving}
            className={POPUP_STYLES.button}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
